import { LazyLoadComponentConfig } from '@se-po/shared-util'
import { AdminPermissionsSkeletonComponent } from '@se-po/shared-feature-admin-permissions/skeleton'
import { ProfileContactsSkeletonComponent } from '@se-po/shared-feature-profile-contacts/skeleton'
import { ProfileFinancialsSkeletonComponent } from '@se-po/shared-feature-profile-financials/skeleton'
import { ProfileCredentialsSkeletonComponent } from '@se-po/shared-feature-profile-credentials/skeleton'
import { ProfileDibsSkeletonComponent } from '@se-po/shared-feature-profile-dibs/skeleton'
import { ProfileGroupsSkeletonComponent } from '@se-po/shared-feature-profile-groups/skeleton'
import { ProfileMembershipsSkeletonComponent } from '@se-po/shared-feature-profile-memberships/skeleton'
import { ProfileMedicalInfoSkeletonComponent } from '@se-po/shared-feature-profile-medical-info/skeleton'
import { ProfileMessageHistorySkeletonComponent } from '@se-po/shared-feature-profile-message-history/skeleton'
import { ProfilePlayerInfoSkeletonComponent } from '@se-po/shared-feature-profile-player-info/skeleton'
import { ProfileRegistrationsSkeletonComponent } from '@se-po/shared-feature-profile-registrations/skeleton'
import { ProfileRosterHistorySkeletonComponent } from '@se-po/shared-feature-profile-roster-history/skeleton'
import { ProfileTeamsSkeletonComponent } from '@se-po/shared-feature-profile-teams/skeleton'

export const PROFILE_SIDE_PANEL_MODULES = [
  'contacts',
  'permissions',
  'medicalInfo',
  'playerInfo',
  'registrations',
  'memberships',
  'teams',
  'financials',
  'credentials',
  'groups',
  'messageHistory',
  'rosterHistory',
  'dibs',
] as const

export type ProfileSidePanelModule = (typeof PROFILE_SIDE_PANEL_MODULES)[number]

// Display order for released modules. Visibility is resolved from the profile access policy.
export const PROFILE_MODULE_ORDER: ProfileSidePanelModule[] = [
  'contacts',
  'permissions',
  'registrations',
  'memberships',
  'teams',
  'rosterHistory',
  'medicalInfo',
  'dibs',
  'credentials',
  'groups',
  'financials',
]

export const MODULES: Partial<Record<ProfileSidePanelModule, LazyLoadComponentConfig>> = {
  contacts: {
    import: () => import('@se-po/shared-feature-profile-contacts'),
    component: 'ProfileContactsComponent',
    module: 'SharedFeatureProfileContactsModule',
    skeleton: ProfileContactsSkeletonComponent,
  },
  permissions: {
    import: () => import('@se-po/shared-feature-admin-permissions'),
    component: 'AdminPermissionsComponent',
    module: 'SharedFeatureAdminPermissionsModule',
    skeleton: AdminPermissionsSkeletonComponent,
    /*
      DATA REQUIREMENTS

      - org with root orgs (for additionalPermision label)
      - admin_management
      - org policy (access_grant_permissions)
      - config (not sure if this is really needed anymore)

      ACTIONS
      - edit permissions (admin_management record)
      - potentially create admin management record for non-admin
    */
  },
  medicalInfo: {
    import: () => import('@se-po/shared-feature-profile-medical-info'),
    component: 'ProfileMedicalInfoComponent',
    module: 'SharedFeatureProfileMedicalInfoModule',
    skeleton: ProfileMedicalInfoSkeletonComponent,
  },
  playerInfo: {
    import: () => import('@se-po/shared-feature-profile-player-info'),
    component: 'ProfilePlayerInfoComponent',
    module: 'SharedFeatureProfilePlayerInfoModule',
    skeleton: ProfilePlayerInfoSkeletonComponent,
  },
  registrations: {
    import: () => import('@se-po/shared-feature-profile-registrations'),
    component: 'ProfileRegistrationsComponent',
    module: 'SharedFeatureProfileRegistrationsModule',
    skeleton: ProfileRegistrationsSkeletonComponent,
  },
  memberships: {
    import: () => import('@se-po/shared-feature-profile-memberships'),
    component: 'ProfileMembershipsComponent',
    module: 'SharedFeatureProfileMembershipsModule',
    skeleton: ProfileMembershipsSkeletonComponent,
    waitForContentReady: true,
  },
  financials: {
    import: () => import('@se-po/shared-feature-profile-financials'),
    component: 'ProfileFinancialsComponent',
    module: 'SharedFeatureProfileFinancialsModule',
    skeleton: ProfileFinancialsSkeletonComponent,
  },
  credentials: {
    import: () => import('@se-po/shared-feature-profile-credentials'),
    component: 'ProfileCredentialsComponent',
    module: 'SharedFeatureProfileCredentialsModule',
    skeleton: ProfileCredentialsSkeletonComponent,
    waitForContentReady: true,
  },
  groups: {
    import: () => import('@se-po/shared-feature-profile-groups'),
    component: 'ProfileGroupsComponent',
    module: 'SharedFeatureProfileGroupsModule',
    skeleton: ProfileGroupsSkeletonComponent,
    waitForContentReady: true,
  },
  messageHistory: {
    import: () => import('@se-po/shared-feature-profile-message-history'),
    component: 'ProfileMessageHistoryComponent',
    module: 'SharedFeatureProfileMessageHistoryModule',
    skeleton: ProfileMessageHistorySkeletonComponent,
  },
  teams: {
    import: () => import('@se-po/shared-feature-profile-teams'),
    component: 'ProfileTeamsComponent',
    module: 'SharedFeatureProfileTeamsModule',
    skeleton: ProfileTeamsSkeletonComponent,
    waitForContentReady: true,
  },
  rosterHistory: {
    import: () => import('@se-po/shared-feature-profile-roster-history'),
    component: 'ProfileRosterHistoryComponent',
    module: 'SharedFeatureProfileRosterHistoryModule',
    skeleton: ProfileRosterHistorySkeletonComponent,
  },
  dibs: {
    import: () => import('@se-po/shared-feature-profile-dibs'),
    component: 'ProfileDibsComponent',
    module: 'SharedFeatureProfileDibsModule',
    skeleton: ProfileDibsSkeletonComponent,
  },
}
